import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    VideoCameraIcon,
    AcademicCapIcon,
    LockClosedIcon,
    LockOpenIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowLeftIcon,
    SparklesIcon,
    PlayIcon,
    PauseIcon,
    BackwardIcon,
    ArrowPathIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';

function formatTime(secs) {
    if (!secs || isNaN(secs)) return '00:00';
    const s = Math.floor(secs);
    const mins = Math.floor(s / 60);
    const remainingSecs = s % 60;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;

    if (hours > 0) {
        return `${hours}:${remainingMins < 10 ? '0' : ''}${remainingMins}:${remainingSecs < 10 ? '0' : ''}${remainingSecs}`;
    }
    return `${remainingMins < 10 ? '0' : ''}${remainingMins}:${remainingSecs < 10 ? '0' : ''}${remainingSecs}`;
}

function parseArrayOptions(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
        try {
            let parsed = JSON.parse(val);
            if (typeof parsed === 'string') {
                try { parsed = JSON.parse(parsed); } catch {}
            }
            if (Array.isArray(parsed)) return parsed;
        } catch {}
    }
    return [];
}

function parseObject(val) {
    if (!val) return {};
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) return val;
    if (typeof val === 'string') {
        try {
            let parsed = JSON.parse(val);
            if (typeof parsed === 'string') {
                try { parsed = JSON.parse(parsed); } catch {}
            }
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) return parsed;
        } catch {}
    }
    return {};
}

export default function InternModuleView() {
    const { id: moduleId } = useParams();
    const [moduleData, setModuleData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Video progress & tracking state
    const [videoWatched, setVideoWatched] = useState(false);
    const [maxTimeWatched, setMaxTimeWatched] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(100);

    // Quiz form state
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submissionResult, setSubmissionResult] = useState(null);

    // Module duration timer state
    const [timerStartedAt, setTimerStartedAt] = useState(null);
    const [moduleRemainingSecs, setModuleRemainingSecs] = useState(null);
    const [retaking, setRetaking] = useState(false);

    // YouTube iFrame Player Ref
    const playerRef = useRef(null);
    const maxTimeRef = useRef(0);

    useEffect(() => {
        fetchModule();
    }, [moduleId]);

    const fetchModule = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`/learning-modules/${moduleId}`);
            if (res.data.success) {
                setModuleData(res.data.data);
                const sub = res.data.data.submission;
                if (sub) {
                    setVideoWatched(Boolean(sub.video_watched));
                    setMaxTimeWatched(sub.max_time_watched || 0);
                    maxTimeRef.current = sub.max_time_watched || 0;
                    if (sub.timer_started_at) {
                        setTimerStartedAt(new Date(sub.timer_started_at));
                    }
                }
            }
        } catch (err) {
            toast.error('Failed to load module details');
        } finally {
            setLoading(false);
        }
    };

    // Load YouTube iFrame API dynamically
    useEffect(() => {
        if (!moduleData || !moduleData.youtube_video_id) return;

        const initPlayer = () => {
            if (window.YT && window.YT.Player) {
                createYTPlayer();
            } else {
                window.onYouTubeIframeAPIReady = () => {
                    createYTPlayer();
                };
                if (!document.getElementById('youtube-iframe-api-script')) {
                    const tag = document.createElement('script');
                    tag.id = 'youtube-iframe-api-script';
                    tag.src = 'https://www.youtube.com/iframe_api';
                    const firstScriptTag = document.getElementsByTagName('script')[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                }
            }
        };

        const createYTPlayer = () => {
            if (playerRef.current) return;
            playerRef.current = new window.YT.Player('youtube-player-iframe', {
                events: {
                    onStateChange: handlePlayerStateChange,
                    onReady: (event) => {
                        const dur = event.target.getDuration();
                        if (dur) setDuration(dur);
                    }
                }
            });
        };

        initPlayer();
    }, [moduleData]);

    // Continuous time tracker & anti-forwarding enforcement interval
    useEffect(() => {
        const interval = setInterval(() => {
            if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
                try {
                    const curr = playerRef.current.getCurrentTime() || 0;
                    const dur = playerRef.current.getDuration() || 0;
                    if (dur > 0) setDuration(dur);
                    setCurrentTime(curr);

                    // Anti-Forwarding Rule:
                    // If current time jumped ahead of maxTimeWatched + 1.5 seconds, force seek back to maxTimeWatched!
                    if (curr > maxTimeRef.current + 1.5) {
                        playerRef.current.seekTo(maxTimeRef.current, true);
                        toast.error('Fast-forwarding is disabled. You can rewind to review previous sections.', { id: 'anti-seek-toast' });
                        return;
                    }

                    // Update Max Time Watched
                    if (curr > maxTimeRef.current) {
                        maxTimeRef.current = curr;
                        setMaxTimeWatched(curr);
                    }

                    // Check if reached end of video (within 2.5 seconds of duration)
                    if (dur > 0 && curr >= dur - 2.5 && !videoWatched) {
                        markVideoCompleted();
                    }
                } catch (e) {}
            }
        }, 300);

        return () => clearInterval(interval);
    }, [videoWatched]);

    // Trigger module timer start on first play
    const triggerTimerStart = async () => {
        if (timerStartedAt) return;
        const now = new Date();
        setTimerStartedAt(now);
        try {
            await axios.post(`/learning-modules/${moduleId}/progress`, {
                video_watched: videoWatched,
                max_time_watched: maxTimeWatched
            });
        } catch (e) {}
    };

    // Countdown interval for overall module duration
    useEffect(() => {
        if (!timerStartedAt || !moduleData?.duration_minutes) return;

        const totalSecs = Number(moduleData.duration_minutes) * 60;

        const updateTimer = () => {
            const elapsed = Math.floor((Date.now() - new Date(timerStartedAt).getTime()) / 1000);
            const remaining = Math.max(0, totalSecs - elapsed);
            setModuleRemainingSecs(remaining);
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [timerStartedAt, moduleData]);

    const formatModuleTimer = (secs) => {
        if (secs === null || secs === undefined) return '00:00';
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
    };

    // Handle Player State Changes
    const handlePlayerStateChange = (event) => {
        const state = event.data;
        if (state === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
            triggerTimerStart();
        } else if (state === window.YT.PlayerState.PAUSED) {
            setIsPlaying(false);
        } else if (state === window.YT.PlayerState.ENDED) {
            setIsPlaying(false);
            markVideoCompleted();
        }
    };

    // Custom Controls Handlers
    const handlePlayPause = () => {
        if (!playerRef.current) return;
        if (isPlaying) {
            playerRef.current.pauseVideo();
        } else {
            playerRef.current.playVideo();
            triggerTimerStart();
        }
    };

    const handleRewind10 = () => {
        if (!playerRef.current) return;
        const curr = playerRef.current.getCurrentTime() || 0;
        const target = Math.max(0, curr - 10);
        playerRef.current.seekTo(target, true);
        setCurrentTime(target);
    };

    const handleRestart = () => {
        if (!playerRef.current) return;
        playerRef.current.seekTo(0, true);
        setCurrentTime(0);
    };

    const handleSeekChange = (e) => {
        if (!playerRef.current) return;
        const val = parseFloat(e.target.value);
        if (val > maxTimeRef.current + 1) {
            toast.error('Fast-forwarding is disabled. You can rewind to any previously watched section.', { id: 'anti-seek-toast' });
            playerRef.current.seekTo(maxTimeRef.current, true);
            setCurrentTime(maxTimeRef.current);
            return;
        }
        playerRef.current.seekTo(val, true);
        setCurrentTime(val);
    };

    const handleToggleMute = () => {
        if (!playerRef.current) return;
        if (isMuted) {
            playerRef.current.unMute();
            setIsMuted(false);
        } else {
            playerRef.current.mute();
            setIsMuted(true);
        }
    };

    const handleVolumeChange = (e) => {
        if (!playerRef.current) return;
        const val = parseInt(e.target.value, 10);
        setVolume(val);
        playerRef.current.setVolume(val);
        if (val === 0) {
            setIsMuted(true);
        } else if (isMuted) {
            playerRef.current.unMute();
            setIsMuted(false);
        }
    };

    // Mark video as completed and notify backend
    const markVideoCompleted = async () => {
        if (videoWatched) return;
        setVideoWatched(true);
        toast.success('🎉 Video completed! Module questions are now unlocked below.');

        try {
            await axios.post(`/learning-modules/${moduleId}/progress`, {
                video_watched: true,
                max_time_watched: duration || maxTimeRef.current
            });
        } catch (err) {
            console.error('Failed to sync video completion:', err);
        }
    };

    // Handle MCQ Option Selection
    const handleOptionSelect = (qId, optionIdx) => {
        setAnswers((prev) => ({ ...prev, [qId]: optionIdx }));
    };

    // Submit Quiz Answers
    const handleSubmitQuiz = async (e) => {
        e.preventDefault();
        const questions = moduleData?.questions || [];
        const missing = questions.filter((q) => answers[q.id] === undefined);

        if (missing.length > 0) {
            return toast.error(`Please answer all questions before submitting (${missing.length} remaining)`);
        }

        try {
            setSubmitting(true);
            const res = await axios.post(`/learning-modules/${moduleId}/submit`, { answers });
            if (res.data.success) {
                toast.success('Quiz submitted and auto-graded!');
                setSubmissionResult(res.data.data.submission);
                fetchModule(); // Refresh to get full graded feedback
            }
        } catch (err) {
            // The mentor edited the quiz while it was open, so the answers no
            // longer line up. No attempt was used: reload the current questions
            // and let the intern answer again.
            if (err.response?.data?.code === 'QUIZ_CHANGED') {
                toast.error(err.response.data.message, { duration: 8000 });
                setAnswers({});
                fetchModule();
                return;
            }
            toast.error(err.response?.data?.message || 'Failed to submit quiz');
        } finally {
            setSubmitting(false);
        }
    };

    // Redo / Retake Quiz Handler
    const handleRetakeQuiz = async () => {
        if (!window.confirm('Are you sure you want to redo this quiz? Your previous attempt will be reset.')) return;
        try {
            setRetaking(true);
            const res = await axios.post(`/learning-modules/${moduleId}/retake`);
            if (res.data.success) {
                toast.success('Quiz reset! You can now re-attempt the quiz questions.');
                setAnswers({});
                fetchModule();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to reset quiz');
        } finally {
            setRetaking(false);
        }
    };

    if (loading) {
        return (
            <div className="py-12 flex flex-col items-center justify-center space-y-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-700"></div>
                <p className="text-sm text-ink-500 font-medium">Loading video module...</p>
            </div>
        );
    }

    if (!moduleData) {
        return (
            <div className="p-8 text-center space-y-4">
                <p className="text-ink-600">Module not found or access restricted.</p>
                <Link to="/learning" className="btn-secondary inline-flex items-center gap-2">
                    <ArrowLeftIcon className="w-4 h-4" /> Back to Learning
                </Link>
            </div>
        );
    }

    const isSubmitted = moduleData.submission?.is_submitted;
    const questions = moduleData.questions || [];

    return (
        <div className="w-full space-y-6 pb-16">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Link to="/learning-modules" className="p-2 rounded-xl bg-white border border-border hover:bg-surface text-ink-700 transition-colors">
                    <ArrowLeftIcon className="w-5 h-5" />
                </Link>
                <div>
                    <h2 className="text-2xl font-bold text-ink-950">{moduleData.title}</h2>
                    <p className="text-xs text-ink-500">Created by {moduleData.mentor ? `${moduleData.mentor.first_name} ${moduleData.mentor.last_name}` : 'Mentor'}</p>
                </div>
            </div>

            {/* Main Video Section */}
            <div className="card p-6 border border-border space-y-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
                    <div className="flex items-center gap-2">
                        <VideoCameraIcon className="w-5 h-5 text-brand-700" />
                        <h4 className="font-bold text-sm text-brand-950 uppercase tracking-wider">Video Lesson</h4>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-brand-50 text-brand-800 border border-brand-200 flex items-center gap-1.5">
                            <ShieldCheckIcon className="w-4 h-4 text-brand-700" />
                            Anti-Forwarding Protection Active
                        </span>
                        {videoWatched ? (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                                <CheckCircleIcon className="w-4 h-4" /> Video Completed
                            </span>
                        ) : (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                In Progress
                            </span>
                        )}
                    </div>
                </div>

                {/* Description */}
                {moduleData.description && (
                    <p className="text-xs text-ink-700 leading-relaxed whitespace-pre-line bg-surface p-3 rounded-xl border border-border">
                        {moduleData.description}
                    </p>
                )}

                {/* Embedded Protected YouTube iFrame Player */}
                <div className="relative aspect-video rounded-2xl overflow-hidden bg-black border border-border shadow-md group">
                    {/* 100% TRANSPARENT SHIELD OVER THE ENTIRE VIDEO PLAYER */}
                    <div
                        className="absolute inset-0 z-30 bg-transparent pointer-events-auto cursor-default"
                        title="Video player locked to platform controls below"
                    />

                    {/* YouTube iFrame Container with controls=0, disablekb=1, modestbranding=1 */}
                    <iframe
                        id="youtube-player-iframe"
                        src={`https://www.youtube.com/embed/${moduleData.youtube_video_id}?enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}&controls=0&disablekb=1&fs=0&modestbranding=1&rel=0&iv_load_policy=3&showinfo=0`}
                        title={moduleData.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                </div>

                {/* Custom Platform Video Control Bar */}
                <div className="p-4 bg-ink-950 text-white rounded-2xl border border-ink-800 space-y-3 shadow-lg">
                    {/* Progress Slider & Timestamps */}
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs text-brand-200 font-mono">
                            <span>{formatTime(currentTime)}</span>
                            <span>Unlocked: {formatTime(maxTimeWatched)} / Total: {formatTime(duration)}</span>
                        </div>

                        {/* Custom Anti-Forward Slider */}
                        <div className="relative flex items-center">
                            <input
                                type="range"
                                min={0}
                                max={duration || 100}
                                step={0.1}
                                value={currentTime}
                                onChange={handleSeekChange}
                                className="w-full h-2 bg-ink-800 rounded-lg appearance-none cursor-pointer accent-brand-500"
                            />
                        </div>
                    </div>

                    {/* Control Buttons Bar */}
                    <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
                        <div className="flex items-center gap-3">
                            {/* Play / Pause Toggle Button */}
                            <button
                                type="button"
                                onClick={handlePlayPause}
                                className="py-2 px-4 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold flex items-center gap-2 transition-all shadow-md"
                            >
                                {isPlaying ? (
                                    <>
                                        <PauseIcon className="w-4 h-4" /> Pause Video
                                    </>
                                ) : (
                                    <>
                                        <PlayIcon className="w-4 h-4" /> Play / Resume
                                    </>
                                )}
                            </button>

                            {/* Backward 10 Seconds */}
                            <button
                                type="button"
                                onClick={handleRewind10}
                                className="py-2 px-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors border border-white/10"
                                title="Rewind 10 Seconds"
                            >
                                <BackwardIcon className="w-4 h-4" /> Rewind 10s
                            </button>

                            {/* Restart Video */}
                            <button
                                type="button"
                                onClick={handleRestart}
                                className="py-2 px-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors border border-white/10"
                                title="Restart Video"
                            >
                                <ArrowPathIcon className="w-4 h-4" /> Restart
                            </button>
                        </div>

                        {/* Volume Controls */}
                        <div className="flex items-center gap-2.5 text-xs">
                            <button
                                type="button"
                                onClick={handleToggleMute}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                            >
                                {isMuted ? <SpeakerXMarkIcon className="w-4 h-4 text-rose-400" /> : <SpeakerWaveIcon className="w-4 h-4" />}
                            </button>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={isMuted ? 0 : volume}
                                onChange={handleVolumeChange}
                                className="w-20 h-1.5 bg-ink-800 rounded-lg appearance-none cursor-pointer accent-brand-400"
                            />
                        </div>
                    </div>
                </div>

                {/* Video Notice & Module Timer Banner */}
                <div className="space-y-2">
                    <div className="p-3 bg-brand-50/50 rounded-xl border border-brand-100 flex flex-wrap items-center justify-between gap-4 text-xs">
                        <span className="text-brand-900 font-medium flex items-center gap-1.5">
                            💡 Watch the video completely. Questions below will automatically unlock once the video is finished.
                        </span>
                        <span className="text-ink-500 font-mono text-[11px] shrink-0">
                            Max Watched: {formatTime(maxTimeWatched)}
                        </span>
                    </div>

                    {timerStartedAt && moduleRemainingSecs !== null && (
                        <div className={`p-3 rounded-xl border flex items-center justify-between gap-3 text-xs shadow-xs font-mono transition-all ${moduleRemainingSecs === 0 ? 'bg-rose-50 border-rose-300 text-rose-900 font-bold' : 'bg-amber-50/80 border-amber-200 text-amber-900 font-bold'}`}>
                            <span className="flex items-center gap-1.5 font-sans font-bold">
                                ⏱️ Module Duration Countdown ({moduleData.duration_minutes || 30} Mins):
                            </span>
                            <span className="text-sm px-2.5 py-0.5 rounded bg-white border font-bold text-amber-950 animate-pulse">
                                {moduleRemainingSecs === 0 ? '⏰ Module Time Expired!' : formatModuleTimer(moduleRemainingSecs)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Questions / Quiz Section (Gated by Video Completion) */}
            <div className="card p-6 border border-border space-y-6 shadow-sm">
                <div className="flex items-center justify-between border-b border-border pb-3">
                    <div className="flex items-center gap-2">
                        <AcademicCapIcon className="w-5 h-5 text-brand-700" />
                        <h4 className="font-bold text-sm text-brand-950 uppercase tracking-wider">Module Quiz & Assessment</h4>
                    </div>

                    {!videoWatched && !isSubmitted ? (
                        <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-bold flex items-center gap-1.5">
                            <LockClosedIcon className="w-4 h-4" /> Locked (Watch Video First)
                        </span>
                    ) : (
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold flex items-center gap-1.5">
                            <LockOpenIcon className="w-4 h-4" /> Unlocked
                        </span>
                    )}
                </div>

                {/* Locked State Banner */}
                {!videoWatched && !isSubmitted ? (
                    <div className="p-8 text-center bg-surface rounded-2xl border border-dashed border-border space-y-3">
                        <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-700 mx-auto flex items-center justify-center">
                            <LockClosedIcon className="w-7 h-7" />
                        </div>
                        <h5 className="font-bold text-ink-950 text-base">Quiz Questions Locked</h5>
                        <p className="text-xs text-ink-500 max-w-md mx-auto">
                            The questions for this module will automatically unlock once you finish watching the complete video above.
                        </p>
                    </div>
                ) : (
                    /* Unlocked Questions Form / Submission Results */
                    <div>
                        {/* Auto-Graded Results Screen */}
                        {isSubmitted && moduleData.submission ? (() => {
                            const passThreshold = moduleData.passing_percentage !== undefined ? Number(moduleData.passing_percentage) : 90;
                            const userPercentage = Number(moduleData.submission.percentage || 0);
                            const isPassed = moduleData.submission.is_passed !== undefined
                                ? Boolean(moduleData.submission.is_passed)
                                : userPercentage >= passThreshold;

                            return (
                                <div className="space-y-6">
                                    {/* Overall Score & Pass/Fail Banner */}
                                    <div className={`p-6 rounded-2xl shadow-md text-white flex flex-wrap items-center justify-between gap-4 ${isPassed ? 'bg-gradient-to-r from-emerald-950 via-emerald-900 to-teal-900 border border-emerald-700' : 'bg-gradient-to-r from-rose-950 via-rose-900 to-red-900 border border-rose-700'}`}>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs uppercase tracking-wider font-bold text-white/80">
                                                    {moduleData.module_type === 'training' ? '🛠️ Training Module Assessment' : '📘 Learning Module Assessment'}
                                                </span>
                                                <span className="text-[10px] px-2 py-0.5 rounded bg-white/20 font-mono text-white">
                                                    Required Pass: {passThreshold}%
                                                </span>
                                            </div>
                                            <h3 className="text-2xl font-bold text-white mt-1 flex items-center gap-2">
                                                Score: {moduleData.submission.score} / {moduleData.submission.total_questions} ({userPercentage}%)
                                            </h3>

                                            {isPassed && moduleData.learningPathItem && (
                                                <div className="mt-2 text-xs text-emerald-200 flex items-center gap-2 font-medium">
                                                    <CheckCircleIcon className="w-4 h-4 text-emerald-400 shrink-0" />
                                                    <span>Module <strong>{moduleData.learningPathItem.title}</strong> marked <strong>Completed</strong> on Learning & Training page!</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <div className="px-4 py-2 bg-white/10 rounded-xl border border-white/20 text-center">
                                                <div className="text-[10px] uppercase font-bold text-white/80">Status</div>
                                                <div className={`text-base font-bold flex items-center gap-1 mt-0.5 ${isPassed ? 'text-emerald-300' : 'text-rose-300'}`}>
                                                    {isPassed ? (
                                                        <>
                                                            <CheckCircleIcon className="w-5 h-5 text-emerald-400" /> PASSED
                                                        </>
                                                    ) : (
                                                        <>
                                                            <XCircleIcon className="w-5 h-5 text-rose-400" /> FAILED
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {!isPassed && (
                                                <div className="flex flex-col items-end gap-1">
                                                    {(moduleData.submission?.attempts_count || 1) < 4 ? (
                                                        <>
                                                            <button
                                                                onClick={handleRetakeQuiz}
                                                                disabled={retaking}
                                                                className="btn-primary py-2.5 px-4 bg-white text-rose-950 hover:bg-rose-50 font-bold shadow-lg flex items-center gap-2"
                                                            >
                                                                <ArrowPathIcon className="w-4 h-4" />
                                                                {retaking ? 'Resetting...' : `Redo Quiz (Attempt ${(moduleData.submission?.attempts_count || 1) + 1} of 4)`}
                                                            </button>
                                                            <span className="text-[10px] text-white/80 font-medium">
                                                                {4 - (moduleData.submission?.attempts_count || 1)} retry remaining (Questions from Redo Section {moduleData.submission?.attempts_count || 1})
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="px-3 py-1.5 bg-rose-900/80 text-rose-200 border border-rose-600 rounded-xl text-xs font-bold">
                                                            ⚠️ Maximum retries reached (3/3 retries used)
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                {/* Graded Questions Detail */}
                                <div className="space-y-4">
                                    <h4 className="font-bold text-xs uppercase tracking-wider text-ink-950">Detailed Feedback & Solutions</h4>
                                    {moduleData?.submission?.review_unavailable && (
                                        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 leading-relaxed">
                                            <span className="font-bold block mb-0.5">Per-question breakdown unavailable</span>
                                            This attempt was taken before the quiz was last updated, so individual
                                            answers can no longer be lined up with the current questions. Your
                                            recorded score above is unaffected.
                                        </div>
                                    )}
                                    {questions.map((q, qIdx) => {
                                        const parsedAnswers = parseObject(moduleData?.submission?.answers);
                                        const rawUserAns = parsedAnswers[q.id] !== undefined ? parsedAnswers[q.id] : parsedAnswers[String(q.id)];
                                        const userAnsIndex = (rawUserAns !== undefined && rawUserAns !== null) ? Number(rawUserAns) : null;
                                        const correctOptIndex = (q.correct_option_index !== undefined && q.correct_option_index !== null) ? Number(q.correct_option_index) : null;
                                        // Trust the server's verdict. While retries remain the answer
                                        // key is withheld, so deriving correctness here marked every
                                        // question wrong, including the ones the intern got right.
                                        const isCorrect = q.question_type === 'mcq'
                                            ? (q.is_correct !== undefined && q.is_correct !== null
                                                ? Boolean(q.is_correct)
                                                : (userAnsIndex !== null && correctOptIndex !== null && userAnsIndex === correctOptIndex))
                                            : true;

                                        return (
                                            <div
                                                key={q.id}
                                                className={`p-5 rounded-2xl border space-y-3 ${moduleData?.submission?.review_unavailable
                                                    ? 'bg-surface border-border'
                                                    : (isCorrect ? 'bg-emerald-50/40 border-emerald-200' : 'bg-rose-50/30 border-rose-200')}`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <h5 className="font-bold text-sm text-ink-950">
                                                        Q{qIdx + 1}: {q.question_text}
                                                    </h5>
                                                    {!moduleData?.submission?.review_unavailable && (
                                                        <span className={`px-3 py-1 rounded-full text-xs font-bold shrink-0 flex items-center gap-1 ${isCorrect ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-rose-100 text-rose-800 border border-rose-300'}`}>
                                                            {isCorrect ? (
                                                                <>
                                                                    <CheckCircleIcon className="w-4 h-4 text-emerald-700" /> Correct
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <XCircleIcon className="w-4 h-4 text-rose-700" /> Incorrect
                                                                </>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* MCQ Choices Feedback */}
                                                {q.question_type === 'mcq' && (
                                                    <div className="space-y-2 pt-1">
                                                        {parseArrayOptions(q.options).map((opt, optIdx) => {
                                                            const isUserChosen = userAnsIndex !== null && userAnsIndex === optIdx;
                                                            const isCorrectOpt = correctOptIndex !== null && correctOptIndex === optIdx;
                                                            // The key may be withheld; the chosen option is still
                                                            // known to be right when the server said so.
                                                            const chosenAndRight = isUserChosen && isCorrect;

                                                            let styleClasses = 'bg-white border-border text-ink-700';
                                                            if (isCorrectOpt || chosenAndRight) {
                                                                styleClasses = 'bg-emerald-50 border-emerald-400 text-emerald-950 font-bold';
                                                            } else if (isUserChosen) {
                                                                styleClasses = 'bg-rose-50 border-rose-400 text-rose-950 font-bold line-through';
                                                            }

                                                            return (
                                                                <div
                                                                    key={optIdx}
                                                                    className={`p-3 rounded-xl border text-xs flex items-center justify-between ${styleClasses}`}
                                                                >
                                                                    <span>{opt}</span>
                                                                    {isCorrectOpt && (
                                                                        <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded border border-emerald-300">
                                                                            ✓ Correct Answer
                                                                        </span>
                                                                    )}
                                                                    {chosenAndRight && !isCorrectOpt && (
                                                                        <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded border border-emerald-300">
                                                                            ✓ Your Choice
                                                                        </span>
                                                                    )}
                                                                    {isUserChosen && !isCorrectOpt && !chosenAndRight && (
                                                                        <span className="text-[11px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded border border-rose-300">
                                                                            ✕ Your Choice
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Explanation */}
                                                {q.explanation && (
                                                    <div className="p-3 bg-white/80 rounded-xl border border-border/60 text-xs text-ink-800 leading-relaxed">
                                                        <span className="font-bold text-brand-800 block mb-0.5">Explanation:</span>
                                                        {q.explanation}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })() : (
                            /* Unsubmitted Quiz Form */
                            <form onSubmit={handleSubmitQuiz} className="space-y-6">
                                {(() => {
                                    const attemptsCount = moduleData.submission?.attempts_count || 1;
                                    const sectionIndex = moduleData.submission?.active_redo_section !== undefined
                                        ? moduleData.submission.active_redo_section
                                        : Math.min(3, Math.max(0, attemptsCount - 1));

                                    return (
                                        <div className="p-4 bg-brand-50/70 border border-brand-200 rounded-2xl flex flex-wrap items-center justify-between gap-3 text-xs shadow-xs">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-brand-950 text-sm">
                                                    {sectionIndex === 0 ? '📌 Initial Attempt (Main Quiz)' : `🔄 Redo Section ${sectionIndex} (Attempt ${attemptsCount} of 4)`}
                                                </span>
                                                <span className="px-2.5 py-0.5 rounded-full bg-brand-600 text-white font-bold text-[11px]">
                                                    {attemptsCount < 4 ? `${4 - attemptsCount} Retries Remaining` : 'Final Attempt'}
                                                </span>
                                            </div>
                                            <span className="text-ink-600 text-[11px] font-medium">
                                                {sectionIndex > 0 ? `Loaded questions from Redo Section ${sectionIndex}` : 'Initial question pool'}
                                            </span>
                                        </div>
                                    );
                                })()}
                                {questions.map((q, qIdx) => (
                                    <div key={q.id} className="p-5 bg-white border border-border rounded-2xl space-y-3 shadow-xs">
                                        <h5 className="font-bold text-sm text-ink-950">
                                            Q{qIdx + 1}: {q.question_text}
                                        </h5>

                                        {q.question_type === 'mcq' ? (
                                            <div className="space-y-2 pt-1">
                                                {parseArrayOptions(q.options).map((opt, optIdx) => {
                                                    const isSelected = answers[q.id] === optIdx;
                                                    return (
                                                        <div
                                                            key={optIdx}
                                                            onClick={() => handleOptionSelect(q.id, optIdx)}
                                                            className={`p-3 rounded-xl border cursor-pointer text-xs flex items-center gap-3 transition-all ${isSelected ? 'bg-brand-50 border-brand-300 text-brand-950 font-bold shadow-xs' : 'bg-surface border-border text-ink-800 hover:bg-white'}`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name={`q_${q.id}`}
                                                                checked={isSelected}
                                                                onChange={() => {}}
                                                                className="text-brand-700 focus:ring-brand-500 w-4 h-4 cursor-pointer"
                                                            />
                                                            <span>{opt}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <textarea
                                                rows={3}
                                                placeholder="Write your answer here..."
                                                value={answers[q.id] || ''}
                                                onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                                                className="input-field w-full text-xs"
                                                required
                                            />
                                        )}
                                    </div>
                                ))}

                                <div className="pt-2 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="btn-primary text-sm py-2.5 px-6 shadow-md"
                                    >
                                        {submitting ? 'Submitting Quiz...' : 'Submit Quiz Answers'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
